import { t, useLocale } from "@/i18n/locale";
import { Message } from "@/i18n/Message";
/**
 * Beta waitlist signup modal, opened from the Home (CapsuleGrid) announcement
 * CTA. Reuses the `.consent-modal-*` frame/title/actions family
 * (SnapshotConfirmDialog, ConsentApprovalModal) — this app has no generic
 * modal component, so the accessibility work below is done here rather than
 * inventing a framework for one dialog. Form-input styling gets its own
 * `.waitlist-form-*` classes (consent-modal never had form fields before).
 *
 * Accessibility (matches/exceeds SnapshotConfirmDialog + DESIGN.md's
 * GlobalCommandPalette contract): role=dialog, aria-modal, aria-labelledby,
 * aria-describedby, Escape closes, overlay click closes, manual Tab trap,
 * focus-in on open, focus-restore on close, background `inert`, body scroll
 * lock, double-submit guard, aria-live error/status announcements, fully
 * keyboard operable (native inputs/buttons only).
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { fetchAccountSession } from "@/auth/accountSession";
import { joinWaitlist, WaitlistRequestError } from "@/api/waitlistClient";
import { recordAnalyticsEvent } from "@/analytics/client";
import type { FailureReason } from "@/analytics/events";
import { loadTurnstileScript } from "@/auth/turnstile";

import { LEGAL_LINKS } from "@/static-site/legal/operator";
import {
  PRO_PRICE_LABEL,
  PRO_PRICE_NOTICE,
  FREE_PRICE_LABEL,
  PRO_WAITLIST_CTA,
  GPU_PRICE_NOTICE,
  PRO_WAITLIST_NOTICE,
} from "./billing/planPolicy";

const TURNSTILE_ACTION = "beta_waitlist_join";

const FOCUSABLE =
  'button:not([disabled]), a[href], input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function errorReasonFor(error: unknown): FailureReason {
  if (!(error instanceof WaitlistRequestError)) return "network_error";
  if (error.code === "network_error") return "network_error";
  if (error.code === "rate_limited") return "rate_limited";
  if (error.code === "validation_error") return "validation_error";
  if (error.code.startsWith("turnstile_")) return "bot_check_failed";
  return "unknown";
}

function friendlyErrorMessage(reason: FailureReason): string {
  switch (reason) {
    case "rate_limited":
      return t("Too many attempts. Please try again in a little while.");
    case "bot_check_failed":
      return t("We couldn't verify you're not a bot. Please try again.");
    case "validation_error":
      return t("Please check your email address and try again.");
    case "network_error":
      return t("Network error. Please check your connection and try again.");
    default:
      return t("Something went wrong. Please try again.");
  }
}

export type WaitlistModalProps = {
  open: boolean;
  onClose: () => void;
  product: "cloud" | "desktop";
  source: "pwa_home" | "pwa_desktop" | "ato_web";
  /** Fired once the signup actually succeeds — the caller persists the
   *  "don't show this again" state (localStorage lives in CapsuleGrid, not
   *  here, so it stays next to the OTHER announcement-visibility state). */
  onSubmitted?: () => void;
  /** The control the modal was opened from, captured by the OPENER at click
   *  time (same reasoning as SnapshotConfirmDialog: a disabled/removed
   *  trigger can't be read reliably from inside this component afterward). */
  returnFocusTo?: HTMLElement | null;
};

export default function WaitlistModal({
  open,
  onClose,
  product,
  source,
  onSubmitted,
  returnFocusTo,
}: WaitlistModalProps) {
  useLocale();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);
  const turnstileTokenRef = useRef<string | undefined>(undefined);

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [useCase, setUseCase] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Reset per-open state, prefill from the account session (if signed in),
  // and fire the "opened" analytics event exactly once per open.
  useEffect(() => {
    if (!open) return;
    setSubmitting(false);
    setSucceeded(false);
    setErrorMessage(null);
    setEmail("");
    setName("");
    setCompany("");
    setUseCase("");
    turnstileTokenRef.current = undefined;

    recordAnalyticsEvent("waitlist_opened", { product, source });

    let cancelled = false;
    fetchAccountSession()
      .then((session) => {
        if (cancelled || !session) return;
        if (session.user.email) setEmail(session.user.email);
        if (product === "desktop" && session.user.name)
          setName(session.user.name);
      })
      .catch(() => {
        // Prefill is a convenience, never a hard requirement.
      });
    return () => {
      cancelled = true;
    };
  }, [open, product, source]);

  // Turnstile widget: renders only when a site key is configured (see
  // routes/beta_waitlist.ts on the API side — the site key and the API's
  // TURNSTILE_SECRET_KEY are a matched pair; until both are configured
  // together, submission fails closed with a clear, retryable error rather
  // than silently succeeding without a bot check).
  useEffect(() => {
    if (!open || succeeded) return;
    const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as
      | string
      | undefined;
    if (!siteKey) return;
    let cancelled = false;
    loadTurnstileScript()
      .then(() => {
        if (cancelled || !turnstileContainerRef.current || !window.turnstile)
          return;
        turnstileWidgetIdRef.current = window.turnstile.render(
          turnstileContainerRef.current,
          {
            sitekey: siteKey,
            action: TURNSTILE_ACTION,
            callback: (token) => {
              turnstileTokenRef.current = token;
            },
            "error-callback": () => {
              turnstileTokenRef.current = undefined;
            },
            "expired-callback": () => {
              turnstileTokenRef.current = undefined;
            },
          },
        );
      })
      .catch(() => {
        // No widget, no token — the API's own fail-closed check surfaces a
        // normal, retryable error; the modal must never crash over this.
      });
    return () => {
      cancelled = true;
      if (turnstileWidgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(turnstileWidgetIdRef.current);
        } catch {
          // best-effort cleanup only
        }
      }
      turnstileWidgetIdRef.current = null;
    };
  }, [open, succeeded]);

  // Focus in, focus restore, background inert, body scroll lock — same
  // lifecycle as GlobalCommandPalette (DESIGN.md §Accessibility contract).
  useEffect(() => {
    if (!open) return;
    const rootEl = document.getElementById("root");
    rootEl?.setAttribute("inert", "");
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();
    return () => {
      rootEl?.removeAttribute("inert");
      document.body.style.overflow = prevOverflow;
      const el = returnFocusTo;
      if (el instanceof HTMLElement && document.contains(el)) el.focus();
    };
    // returnFocusTo is fixed for the lifetime of one open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  function close() {
    if (submitting) return;
    onClose();
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      event.stopPropagation();
      close();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? [],
    );
    if (focusable.length === 0) {
      event.preventDefault();
      dialogRef.current?.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === dialogRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return; // double-submit guard

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setErrorMessage("Email is required.");
      return;
    }

    setSubmitting(true);
    setErrorMessage(null);
    recordAnalyticsEvent("waitlist_submitted", { product, source });

    try {
      // Turnstile also writes the verified token to its standard hidden
      // response input. Read it as a fallback because the documented test
      // widget can populate that input without firing the render callback.
      // Real widgets still use the callback token first.
      const hiddenTurnstileToken =
        turnstileContainerRef.current
          ?.querySelector<HTMLInputElement>(
            'input[name="cf-turnstile-response"]',
          )
          ?.value.trim() || undefined;
      await joinWaitlist({
        email: trimmedEmail,
        name: product === "desktop" ? name.trim() || undefined : undefined,
        company:
          product === "desktop" ? company.trim() || undefined : undefined,
        useCase:
          product === "desktop" ? useCase.trim() || undefined : undefined,
        product,
        source,
        turnstileToken: turnstileTokenRef.current ?? hiddenTurnstileToken,
      });
      setSucceeded(true);
      recordAnalyticsEvent("waitlist_succeeded", { product, source });
      onSubmitted?.();
    } catch (error) {
      const reason = errorReasonFor(error);
      setErrorMessage(friendlyErrorMessage(reason));
      recordAnalyticsEvent("waitlist_failed", { product, source, reason });
    } finally {
      setSubmitting(false);
    }
  }

  // Portalled to document.body — NOT rendered inline where CapsuleGrid places
  // it (inside #root). The lifecycle effect above sets `inert` on #root while
  // open; if this dialog were a DESCENDANT of #root, that would make the
  // dialog itself inert too (unclickable, unfocusable, deaf to keyboard) the
  // instant it opens, since `inert` applies to the whole subtree. Matches
  // GlobalCommandPalette's own portal for the identical reason.
  return createPortal(
    <div
      className="consent-modal-overlay"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) close();
      }}
    >
      <div
        ref={dialogRef}
        className="consent-modal waitlist-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="waitlist-modal-title"
        aria-describedby="waitlist-modal-description"
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <div className="waitlist-modal-header">
          <h3 id="waitlist-modal-title" className="consent-modal-title">
            {product === "cloud"
              ? t(PRO_WAITLIST_CTA)
              : t("Join the Ato Desktop waitlist")}
          </h3>
          <button
            type="button"
            className="waitlist-modal-close"
            onClick={close}
            aria-label={t("Close")}
            disabled={submitting}
          >
            <X size={16} aria-hidden />
          </button>
        </div>
        <p id="waitlist-modal-description" className="consent-modal-note">
          {product === "cloud"
            ? t(PRO_WAITLIST_NOTICE)
            : t("Ato Desktop for macOS is currently in early access. Join the waitlist and we’ll contact you when access is available.")}
        </p>

        {product === "cloud" && (
          <>
            <p className="consent-modal-note"><Message message={"Pro: {{value0}}. Free: {{value1}}."} values={{ value0: PRO_PRICE_LABEL, value1: FREE_PRICE_LABEL }} /></p>
            <p className="consent-modal-note">{t(PRO_PRICE_NOTICE)}</p>
            <p className="consent-modal-note">{t(GPU_PRICE_NOTICE)}</p>
            <p className="consent-modal-note"><Message message={"We use your email to send a Pro launch notification. {{value0}}. {{value1}} to leave the waitlist or request deletion."} values={{ value0: <a
                href={LEGAL_LINKS.privacy}
                target="_blank"
                rel="noopener noreferrer"
              ><Message message={"Privacy"} /></a>, value1: <a
                href={LEGAL_LINKS.contact}
                target="_blank"
                rel="noopener noreferrer"
              ><Message message={"Contact us"} /></a> }} /></p>
          </>
        )}

        {succeeded ? (
          <>
            <p
              className="waitlist-form-success"
              role="status"
              aria-live="polite"
            ><Message message={"You're on the waitlist.{{value0}}"} values={{ value0: product === "cloud" &&
                t(" No subscription or charge has been created. You can keep using Free.") }} /></p>
            <div className="consent-modal-actions">
              <button
                type="button"
                className="consent-modal-approve"
                onClick={close}
              ><Message message={"Close"} /></button>
            </div>
          </>
        ) : (
          <form className="waitlist-form" onSubmit={onSubmit} noValidate>
            <div className="waitlist-form-field">
              <label className="waitlist-form-label" htmlFor="waitlist-email"><Message message={"Email"} /></label>
              <input
                id="waitlist-email"
                className="waitlist-form-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={submitting}
              />
            </div>
            {product === "desktop" && (
              <>
                <div className="waitlist-form-field">
                  <label
                    className="waitlist-form-label"
                    htmlFor="waitlist-name"
                  ><Message message={"Name"} /></label>
                  <input
                    id="waitlist-name"
                    className="waitlist-form-input"
                    type="text"
                    autoComplete="name"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    disabled={submitting}
                  />
                </div>
                <div className="waitlist-form-field">
                  <label
                    className="waitlist-form-label"
                    htmlFor="waitlist-company"
                  ><Message message={"Company"} /></label>
                  <input
                    id="waitlist-company"
                    className="waitlist-form-input"
                    type="text"
                    autoComplete="organization"
                    value={company}
                    onChange={(event) => setCompany(event.target.value)}
                    disabled={submitting}
                  />
                </div>
                <div className="waitlist-form-field">
                  <label
                    className="waitlist-form-label"
                    htmlFor="waitlist-use-case"
                  ><Message message={"What would you like to run locally with Ato Desktop?"} /></label>
                  <textarea
                    id="waitlist-use-case"
                    className="waitlist-form-input waitlist-form-textarea"
                    value={useCase}
                    onChange={(event) => setUseCase(event.target.value)}
                    disabled={submitting}
                  />
                </div>
              </>
            )}

            <div ref={turnstileContainerRef} className="waitlist-turnstile" />

            {errorMessage ? (
              <p className="waitlist-form-error" role="alert">
                {t(errorMessage)}
              </p>
            ) : null}

            <div className="consent-modal-actions">
              <button
                type="submit"
                className="consent-modal-approve"
                disabled={submitting}
              >
                {submitting
                  ? t("Joining…")
                  : product === "cloud"
                    ? t("Notify me when Pro launches")
                    : t("Join the waitlist")}
              </button>
              <button
                type="button"
                className="consent-modal-reject"
                onClick={close}
                disabled={submitting}
              ><Message message={"Cancel"} /></button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
}
